import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";

import classes from "./NotificationsFilters.module.less";
import { getNavigationTree, type NavigationTreeDataDTO } from "~/Notifications/api/getNavigationTree";
import { type NotificationData } from "~/Notifications/Messages";
import { toMapFn } from "~/utils/reducers";
import { useOnMount } from "~/common/hooks/useOnMount";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";

export interface NotificationFilterEntry {
  type: string;
  label: string;
  enabled: boolean;
}

export type Props = {
  filters: NotificationFilterEntry[];
  disabled?: boolean;
  onFilterChange: (filter: NotificationFilterEntry) => void;
};

export const NotificationsFilters = ({ filters, onFilterChange, disabled, ...rest }: Props) => {
  const { t } = useTranslation();

  filters = filters.filter((filter) => filter.type !== "ALL");
  const isAllSelected = filters.length > 0 && filters.every((filter) => filter.enabled);
  const isSomeSelected = filters.some((filter) => filter.enabled);
  const allFilter = {
    type: ALL_FILTER_TYPE,
    label: "quoteListPage.predefinedDate.allTime",
    enabled: isAllSelected,
  };

  return (
    <div className={classes.root}>
      <Accordion expanded={true} slotProps={{ heading: { component: "h4" } }}>
        <AccordionSummary expandIcon={null} className={classes.accordionTitle} sx={{ pointerEvents: "none" }}>
          {t("notifications.filters.header.title")}
        </AccordionSummary>
        <AccordionDetails>
          <CheckboxWithLabel
            key={allFilter.type}
            className={"d-flex flex-align-center"}
            data-test-id={`checkbox-filters-notification-${allFilter.type}`}
            checked={isSomeSelected}
            indeterminate={!allFilter.enabled && isSomeSelected}
            onCheck={() => onFilterChange(allFilter)}
            disabled={disabled}
          >
            {t(allFilter.label)}
          </CheckboxWithLabel>
          {filters.map((filter) => (
            <CheckboxWithLabel
              key={filter.type}
              className={"d-flex flex-align-center"}
              data-test-id={`checkbox-filters-notification-${filter.type}`}
              checked={filter.enabled}
              onCheck={() => onFilterChange(filter)}
              disabled={disabled}
            >
              {t(filter.label)}
            </CheckboxWithLabel>
          ))}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const ALL_FILTER_TYPE = "$ALL$";

export const fetchNavigationFilters = async () => {
  const dto = await getNavigationTree();
  return dto.map(transformNavigationFilter);
};

type SetFiltersFn = Dispatch<SetStateAction<NotificationFilterEntry[]>>;
export function makeOnFilterChange(setFilters: SetFiltersFn) {
  return (filter: NotificationFilterEntry) => {
    setFilters((prevValue) => {
      if (filter.type === ALL_FILTER_TYPE) {
        const newVal = !filter.enabled;
        return prevValue.map((item) => ({ ...item, enabled: newVal }));
      }
      return prevValue.map((item) => (item.type === filter.type ? { ...item, enabled: !item.enabled } : item));
    });
  };
}

type DataProvider = () => Promise<NotificationFilterEntry[]>;
export const useNotificationFilters = (initialDataProvider: DataProvider) => {
  const [filters, setFilters] = useState<NotificationFilterEntry[]>([]);

  useOnMount(() => {
    initialDataProvider().then((initialFilters) => setFilters(initialFilters));
  });

  const onFilterChange = makeOnFilterChange(setFilters);
  const replaceFilters = (filters: NotificationFilterEntry[]) => {
    setFilters(filters);
  };

  return {
    filters,
    changeFilter: onFilterChange,
    replaceFilters,
  };
};

export const useNotificationFiltering = (filters: NotificationFilterEntry[], notifications: NotificationData[]) => {
  return useMemo(() => {
    const isAllSelected = filters.length > 0 && filters.every((filter) => filter.enabled);
    if (isAllSelected) {
      return notifications;
    } else {
      const allowedTypes = toMapFn(
        filters,
        (filter) => filter.type,
        (filter) => filter.enabled,
      );
      return notifications.filter((notification) => allowedTypes[notification.typeCode]);
    }
  }, [filters, notifications]);
};

function transformNavigationFilter(dto: NavigationTreeDataDTO): NotificationFilterEntry {
  return {
    type: dto.parentType,
    label: dto.parentTypeTranslation,
    enabled: true,
  };
}
